import {
  HEADER_PRIMARY_BUTTON,
  HEADER_SECONDARY_BUTTON,
} from "../components/layout/headerActionStyles";
import {
  Bot,
  CalendarClock,
  CheckCircle,
  Clock,
  Globe,
  LayoutGrid,
  List,
  ListFilter,
  Plus,
  Search,
  Send,
  Sparkles,
  User,
  X,
  XCircle,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AIOptimizerMenu,
  Button,
  CommentsList,
  EmptyState,
  Input,
  PageLayout,
  Pagination,
  PostCard,
  PostsModals,
  ResponsiveGrid,
  Select,
} from "../components";
import ViralResearchModal from "../components/modals/ViralResearchModal";
import VisualSuggestionsModal, {
  type VisualSuggestionsResult,
} from "../components/modals/VisualSuggestionsModal";
import PostTableRow from "../components/posts/PostTableRow";
import { PostsSkeleton } from "../components/skeletons";
import { useActiveContext } from "../contexts/ActiveContextContext";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../contexts/LanguageContext";
import { usePosts } from "../hooks/usePosts";
import { usePostsActions } from "../hooks/usePostsActions";
import { usePostsAI } from "../hooks/usePostsAI";
import { usePostsFilters } from "../hooks/usePostsFilters";
import { usePostsMedia } from "../hooks/usePostsMedia";
import { usePostsModals } from "../hooks/usePostsModals";
import { usePostsPagination } from "../hooks/usePostsPagination";
import { usePostsPublishing } from "../hooks/usePostsPublishing";
import { usePostsUtils } from "../hooks/usePostsUtils";
import postsService from "../services/postsService";
import { showToastError, showToastSuccess } from "../shared/alertService";
import apiService from "../shared/apiService";
import { getPostImageUrl } from "../shared/constants";
import { cn } from "../utils/cn";
import { PlatformIcon } from "../utils/platformIcons";
import {
  buildVideoGenerationRequest,
  VideoTextItem,
} from "../utils/videoGenerationRequest";

import type { FilterType } from "../types/posts";

const Posts: React.FC = () => {
  const { t } = useTranslation();
  const { activeContext, isLoading: isContextLoading } = useActiveContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightedPost, setHighlightedPost] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [viewMode, setViewMode] = useState<"cards" | "table">(() => {
    return (
      (localStorage.getItem("postsViewMode") as "cards" | "table") || "cards"
    );
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    localStorage.setItem("postsViewMode", viewMode);
  }, [viewMode]);
  const postRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const highlightHandledRef = useRef(false);
  const itemsPerPage = 8;

  // Hooks modulares
  const filters = usePostsFilters();
  const modals = usePostsModals();
  const utils = usePostsUtils();

  const {
    pendingPosts,
    approvedPosts,
    publishedPosts,
    rejectedPosts,
    scheduledPosts,
    allPosts,
    totalCounts,
    stats,
    loading,
    loadPosts,
    approvePost,
    rejectPost,
    revertToPending,
    editPost,
    schedulePost,
    reschedulePostPublished,
    unschedulePost,
    deletePost,
    uploadImage,
    uploadVideo,
  } = usePosts();

  // Verificação de autenticação
  const { isAuthenticated, token, user } = useAuth();

  // Estados adicionais específicos (sugestões visuais)
  const [showVisualSuggestions, setShowVisualSuggestions] = useState<
    Record<number, VisualSuggestionsResult>
  >({});
  const [visualSuggestionsModalPostId, setVisualSuggestionsModalPostId] =
    useState<number | null>(null);

  const [showViralResearchModal, setShowViralResearchModal] = useState(false);

  // Hydrate suggestions logic below...

  useEffect(() => {
    // Sincronizar filtro com URL
    const filter = searchParams.get("filter") as FilterType;
    if (
      filter &&
      [
        "all",
        "pending",
        "approved",
        "published",
        "rejected",
        "scheduled",
      ].includes(filter)
    ) {
      const newFilter = filter;
      if (newFilter !== filters.activeFilter) {
        filters.setActiveFilter(newFilter);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Suggestions hydration logic moved below...

  // Hidratar sugestões visuais persistidas quando os posts forem carregados
  useEffect(() => {
    type PostWithSuggestions = {
      id?: number;
      visual_suggestions?: VisualSuggestionsResult | null;
    };
    const allPosts: PostWithSuggestions[] = [
      ...pendingPosts,
      ...approvedPosts,
      ...publishedPosts,
      ...rejectedPosts,
      ...scheduledPosts,
    ];
    setShowVisualSuggestions((prev) => {
      const next = { ...prev };
      for (const p of allPosts) {
        if (p?.id == null) continue;
        if (
          p.visual_suggestions != null &&
          typeof p.visual_suggestions === "object"
        ) {
          next[p.id] = p.visual_suggestions as VisualSuggestionsResult;
        } else {
          delete next[p.id];
        }
      }
      return next;
    });
  }, [
    pendingPosts,
    approvedPosts,
    publishedPosts,
    rejectedPosts,
    scheduledPosts,
  ]);

  // Funções utilitárias usando o hook
  const getFilterCount = (filter: FilterType) => {
    // Priorizar totalCounts carregados por paginação ou stats globais
    const serverCount = totalCounts[filter] || (stats?.[filter] ?? 0);

    // Se temos posts carregados localmente, usar count deles se for maior (fallback para caches/filtros locais)
    let localCount = 0;
    switch (filter) {
      case "pending":
        localCount = pendingPosts.length;
        break;
      case "approved":
        localCount = approvedPosts.length;
        break;
      case "published":
        localCount = publishedPosts.length;
        break;
      case "rejected":
        localCount = rejectedPosts.length;
        break;
      case "scheduled":
        localCount = scheduledPosts.length;
        break;
    }

    return Math.max(serverCount, localCount);
  };

  // Posts do status atualmente ativo, sem os filtros locais (plataforma/IA) aplicados
  const postsForActiveStatus = useMemo(() => {
    switch (filters.activeFilter as FilterType | "all") {
      case "pending":
        return pendingPosts;
      case "approved":
        return approvedPosts;
      case "published":
        return publishedPosts;
      case "rejected":
        return rejectedPosts;
      case "scheduled":
        return scheduledPosts;
      case "all":
        return allPosts;
      default:
        return pendingPosts;
    }
  }, [
    filters.activeFilter,
    pendingPosts,
    approvedPosts,
    publishedPosts,
    rejectedPosts,
    scheduledPosts,
    allPosts,
  ]);

  // Conjunto completo (não paginado) do status ativo, usado para calcular os
  // contadores dos filtros de plataforma/autoria — postsForActiveStatus é
  // limitado à página atual (itemsPerPage), o que subestimava esses contadores.
  const [allActiveStatusPosts, setAllActiveStatusPosts] = useState<any[]>([]);

  // Calcular posts atuais e total de itens para paginação. Quando há filtros
  // locais (plataforma/autoria) ativos, a página do servidor (8 itens) não é
  // suficiente para filtrar+paginar corretamente — usamos o conjunto completo
  // (allActiveStatusPosts) e paginamos localmente. Sem filtros locais, usamos
  // a página já paginada pelo servidor.
  const hasLocalFilters =
    filters.platformFilter !== "all" || filters.aiGeneratedFilter !== "all";

  const currentPosts = useMemo(() => {
    const source = hasLocalFilters
      ? allActiveStatusPosts
      : postsForActiveStatus;
    return utils.filterPosts(
      source,
      filters.platformFilter,
      filters.aiGeneratedFilter,
    );
  }, [
    hasLocalFilters,
    allActiveStatusPosts,
    postsForActiveStatus,
    filters.platformFilter,
    filters.aiGeneratedFilter,
    utils,
  ]);

  // Contagens leves (agregadas no backend via COUNT/GROUP BY) para os badges
  // de Plataforma/Autoria — evita buscar até 1000 posts completos só para contar.
  const [countGroups, setCountGroups] = useState<
    { platform: string; ai_generated: boolean; count: number }[]
  >([]);

  useEffect(() => {
    // Esperar o contexto ativo (organização/empresa) estabilizar antes de
    // buscar — evita disparar e descartar buscas repetidas enquanto o
    // contexto ainda está sendo resolvido no carregamento inicial.
    if (isContextLoading) return;

    let cancelled = false;
    const searchTerm = debouncedSearch.trim() || undefined;

    postsService
      .getPostCounts(filters.activeFilter, searchTerm)
      .then(({ groups }) => {
        if (!cancelled) setCountGroups(groups);
      });

    return () => {
      cancelled = true;
    };
    // Refaz a busca quando o contexto ativo (organização/empresa) muda —
    // sem isso, uma busca disparada durante a resolução inicial do contexto
    // (antes de estabilizar) nunca é refeita e os contadores ficam presos
    // em um valor errado/vazio.
  }, [
    filters.activeFilter,
    debouncedSearch,
    activeContext.type,
    activeContext.organization?.id,
    activeContext.clientCompany?.id,
    isContextLoading,
  ]);

  // Busca pesada (até 1000 posts completos) só quando necessária: filtros
  // locais de plataforma/autoria exigem os dados completos para filtrar e
  // paginar no cliente (ver `currentPosts` acima). Sem filtro local, a
  // página do servidor (postsForActiveStatus) já basta.
  useEffect(() => {
    if (isContextLoading || !hasLocalFilters) return;

    let cancelled = false;
    const searchTerm = debouncedSearch.trim() || undefined;

    const fetchAllForLocalFilter = async () => {
      try {
        let result: { posts?: any[] } | undefined;
        switch (filters.activeFilter as FilterType | "all") {
          case "pending":
            result = await postsService.getPendingPosts(
              false,
              1,
              1000,
              searchTerm,
            );
            break;
          case "approved":
            result = await postsService.getApprovedPosts(
              false,
              1,
              1000,
              searchTerm,
            );
            break;
          case "published":
            result = await postsService.getPublishedPosts(
              false,
              1,
              1000,
              searchTerm,
            );
            break;
          case "rejected":
            result = await postsService.getRejectedPosts(1, 1000, searchTerm);
            break;
          case "scheduled":
            result = await postsService.getScheduledPosts(1, 1000, searchTerm);
            break;
          case "all":
            result = await postsService.getAllPosts(false, 1, 1000, searchTerm);
            break;
        }
        if (!cancelled) setAllActiveStatusPosts(result?.posts || []);
      } catch {
        if (!cancelled) setAllActiveStatusPosts([]);
      }
    };

    fetchAllForLocalFilter();
    return () => {
      cancelled = true;
    };
  }, [
    hasLocalFilters,
    filters.activeFilter,
    debouncedSearch,
    activeContext.type,
    activeContext.organization?.id,
    activeContext.clientCompany?.id,
    isContextLoading,
  ]);

  // Contagens para exibir ao lado de cada opção dos filtros, derivadas dos
  // grupos agregados (platform × ai_generated) vindos do backend.
  const platformCounts = useMemo(() => {
    const wantsAi = filters.aiGeneratedFilter;
    const matches = (g: { ai_generated: boolean }) =>
      wantsAi === "all" ||
      (wantsAi === "yes" ? g.ai_generated : !g.ai_generated);

    const counts: Record<string, number> = {
      all: 0,
      linkedin: 0,
      facebook: 0,
      instagram: 0,
    };
    for (const g of countGroups) {
      if (!matches(g)) continue;
      counts.all += g.count;
      if (g.platform in counts) counts[g.platform] += g.count;
    }
    return counts;
  }, [countGroups, filters.aiGeneratedFilter]);

  const aiGeneratedCounts = useMemo(() => {
    const wantsPlatform = filters.platformFilter;
    const matches = (g: { platform: string }) =>
      wantsPlatform === "all" || g.platform === wantsPlatform.toLowerCase();

    let all = 0;
    let yes = 0;
    for (const g of countGroups) {
      if (!matches(g)) continue;
      all += g.count;
      if (g.ai_generated) yes += g.count;
    }
    return { all, yes, no: all - yes };
  }, [countGroups, filters.platformFilter]);

  const statusCounts = useMemo(() => {
    const pending = getFilterCount("pending");
    const approved = getFilterCount("approved");
    const scheduled = getFilterCount("scheduled");
    const published = getFilterCount("published");
    const rejected = getFilterCount("rejected");
    // Preferir o total real do servidor (evita contagem duplicada quando um
    // post agendado também está incluído no bucket "approved", por exemplo).
    // Usar || (não ??): totalCounts.all começa em 0 (não undefined) até que
    // o filtro "all" seja carregado, então 0 deve cair para o próximo fallback.
    const all =
      stats?.total_posts ||
      totalCounts.all ||
      pending + approved + scheduled + published + rejected;
    return {
      all,
      pending,
      approved,
      scheduled,
      published,
      rejected,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    totalCounts,
    stats,
    pendingPosts,
    approvedPosts,
    publishedPosts,
    rejectedPosts,
    scheduledPosts,
  ]);
  // totalItems para a paginação: usar totalCount do servidor se não houver filtros locais,
  // caso contrário usar o tamanho do array filtrado localmente
  const totalItems = useMemo(() => {
    if (!hasLocalFilters && totalCounts[filters.activeFilter]) {
      return totalCounts[filters.activeFilter];
    }
    return currentPosts.length;
  }, [currentPosts.length, filters.activeFilter, totalCounts, hasLocalFilters]);

  // Hook de paginação
  const pagination = usePostsPagination({ totalItems, itemsPerPage });

  // Se estivermos usando paginação do servidor (sem filtros locais),
  // os posts atuais já são a página correta.
  const paginatedPosts = useMemo(() => {
    const needsLocalPagination = debouncedSearch.trim() || hasLocalFilters;

    if (!needsLocalPagination) {
      return currentPosts;
    }

    return currentPosts.slice(pagination.startIndex, pagination.endIndex);
  }, [
    currentPosts,
    pagination.startIndex,
    pagination.endIndex,
    debouncedSearch,
    hasLocalFilters,
  ]);

  // Ref para guardar o último termo de busca usado
  const lastSearchRef = useRef<string>("");

  useEffect(() => {
    // Only load data if authenticated and has token
    // Load active filter status + stats for better performance
    if (isAuthenticated && token) {
      const searchChanged = lastSearchRef.current !== debouncedSearch;

      // Check if we already have data for this status
      const hasData =
        (filters.activeFilter === "pending" && pendingPosts.length > 0) ||
        (filters.activeFilter === "approved" && approvedPosts.length > 0) ||
        (filters.activeFilter === "published" && publishedPosts.length > 0) ||
        (filters.activeFilter === "rejected" && rejectedPosts.length > 0) ||
        (filters.activeFilter === "scheduled" && scheduledPosts.length > 0) ||
        ((filters.activeFilter as FilterType | "all") === "all" &&
          allPosts.length > 0);

      // Fetch data if missing or context changed (enforced by dependency on loadPosts)
      if (!hasData || loading || searchChanged) {
        if (searchChanged) {
          lastSearchRef.current = debouncedSearch;
        }
        loadPosts(
          true,
          [filters.activeFilter],
          pagination.currentPage,
          itemsPerPage,
          debouncedSearch || undefined,
        );
      } else {
        // Se já temos dados, mas a página mudou, carregar a nova página
        // (loadPosts já tem trava interna contra duplicatas)
        loadPosts(
          false,
          [filters.activeFilter],
          pagination.currentPage,
          itemsPerPage,
          debouncedSearch || undefined,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    token,
    filters.activeFilter,
    loadPosts,
    pagination.currentPage,
    itemsPerPage,
    debouncedSearch,
  ]);

  // Resetar página ao mudar filtros (Exceto aba que já resetamos via onClick para evitar race conditions)
  useEffect(() => {
    if (pagination.currentPage !== 1) {
      pagination.setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.platformFilter, filters.aiGeneratedFilter]);

  // Resetar página ao buscar
  useEffect(() => {
    if (searchTerm && pagination.currentPage !== 1) {
      pagination.setCurrentPage(1);
    }
  }, [searchTerm, pagination]);

  // Garantir que a página atual não ultrapasse o total de páginas
  useEffect(() => {
    // Usar o totalCount do servidor para validar, se disponível
    const totalFromServer = totalCounts[filters.activeFilter];

    let totalItemsForValidation = 0;
    if (totalFromServer) {
      totalItemsForValidation = totalFromServer;
    } else {
      // Fallback para contagem local se não tivermos info do servidor
      switch (filters.activeFilter) {
        case "pending":
          totalItemsForValidation = pendingPosts.length;
          break;
        case "approved":
          totalItemsForValidation = approvedPosts.length;
          break;
        case "published":
          totalItemsForValidation = publishedPosts.length;
          break;
        case "rejected":
          totalItemsForValidation = rejectedPosts.length;
          break;
        case "scheduled":
          totalItemsForValidation = scheduledPosts.length;
          break;
      }
    }

    const totalPages = Math.ceil(totalItemsForValidation / itemsPerPage);
    if (pagination.currentPage > totalPages && totalPages > 0) {
      pagination.setCurrentPage(totalPages);
    } else if (totalPages === 0 && pagination.currentPage > 1) {
      pagination.setCurrentPage(1);
    }
  }, [
    pendingPosts.length,
    approvedPosts.length,
    publishedPosts.length,
    totalCounts,
    pagination.currentPage,
    filters.activeFilter,
    itemsPerPage,
  ]);

  useEffect(() => {
    // Detectar post destacado via URL params
    const highlightParam = searchParams.get("highlight");
    if (highlightParam && !highlightHandledRef.current) {
      const postId = parseInt(highlightParam);
      setHighlightedPost(postId);
      // Atualiza campo de busca para que o filtro encontre o post pelo ID
      setSearchTerm(`#${postId}`);
      // Marca que já tratou o highlight para não recarregar novamente
      highlightHandledRef.current = true;
      // Força recarregar os posts com o novo filtro (primeira página)
      loadPosts(true);

      // Rolar até o post após um pequeno delay para garantir que os dados foram carregados
      setTimeout(() => {
        const postElement = postRefs.current[postId];
        if (postElement) {
          postElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });

          // Remover destaque após 5 segundos
          setTimeout(() => {
            setHighlightedPost(null);
            // Limpar parâmetro da URL
            setSearchParams((prev) => {
              const newParams = new URLSearchParams(prev);
              newParams.delete("highlight");
              return newParams;
            });
            // Resetar flag para permitir novo highlight futuro
            highlightHandledRef.current = false;
          }, 5000);
        }
      }, 1000);
    }
  }, [
    searchParams,
    setSearchParams,
    pendingPosts,
    approvedPosts,
    publishedPosts,
  ]);

  // Hook de ações básicas
  const actions = usePostsActions({
    approvePost,
    rejectPost,
    revertToPending,
    editPost,
    schedulePost,
    reschedulePostPublished,
    unschedulePost,
    deletePost,
    uploadImage,
    uploadVideo,
    modals,
    navigate,
  });

  // Hook de mídia (vídeo e imagem)
  const media = usePostsMedia({
    loadPosts,
    stats,
    modals,
  });

  // Hook de publicação
  const publishing = usePostsPublishing({
    loadPosts,
    approvedPosts,
    modals,
    user,
    getMinDate: () => {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    setShowScheduleModal: modals.setShowScheduleModal,
    setScheduleDate: modals.setScheduleDate,
  });

  // Hook de IA
  const ai = usePostsAI({
    loadPosts,
    stats,
    modals,
    setCustomPrompt: media.setCustomPrompt,
    setImageRestrictions: media.setImageRestrictions,
    setImageProvider: media.setImageProvider,
    setImageLogoFile: media.setImageLogoFile,
    setImageLogoPreview: media.setImageLogoPreview,
    setImageReferenceImages: media.setImageReferenceImages,
    setImageReferenceImagePreviews: media.setImageReferenceImagePreviews,
    setShowVisualSuggestions,
    setVisualSuggestionsModalPostId,
  });
  // Handlers de IA estão no hook usePostsAI (ai.enhancePost, ai.generateImage, ai.enhanceImageWithAI, ai.getVisualSuggestions, ai.checkGrammar)

  // Synchronously track context to reset state and avoid flicker
  // Moved here to ensure hooks like pagination are available
  const contextId = `${activeContext.type}-${activeContext.organization?.id || ""}-${activeContext.clientCompany?.id || ""}`;
  const [lastContextId, setLastContextId] = useState(contextId);

  if (contextId !== lastContextId) {
    setShowVisualSuggestions({});
    setLastContextId(contextId);
    // Reset pagination to first page on context change
    pagination.setCurrentPage(1);
  }
  // Handlers de mídia estão no hook usePostsMedia (media.handleUploadVideo, media.handleClearVideo, media.generateVideoWithAI, media.handleEditVideo, media.handleSaveVideoEdits, media.handleUploadImage, media.handleGenerateImage)
  // Handlers de publicação estão no hook usePostsPublishing (publishing.showPublishOptions, publishing.publishPost, publishing.handlePublishConfirm, publishing.handleMultiPlatformPublish, publishing.handleRepublishLinkedIn, publishing.handleRepublishMultiPlatform, publishing.handleReplyToComment, publishing.openMultiPlatformModal, publishing.openUnifiedRepublishModal)

  // Wrappers para compatibilidade (usando hooks)
  const handleEnhancePost = async (postId: number) => {
    await ai.enhancePost(postId);
  };

  const handleReject = async (
    postId: number,
    reason: string,
    successMessage?: string,
  ) => {
    await actions.handleReject(postId, reason, successMessage);
    await loadPosts();
  };

  const handleSchedule = async (postId: number, date: string, time: string) => {
    // Verificar se o post já está publicado (para o fluxo de reagendamento)
    const isPublished = publishedPosts.some((p: any) => p.id === postId);

    if (isPublished) {
      await actions.handleReschedule(postId, date, time);
    } else {
      // O handleSchedule do hook não recebe parâmetros, usa os valores do modal
      modals.setShowScheduleModal(postId);
      modals.setScheduleDate(date);
      modals.setScheduleTime(time);
      await actions.handleSchedule();
    }
  };

  // Removidas funções duplicadas - todas estão nos hooks:
  // - handleUploadVideo, handleClearVideo, generateVideoWithAI, handleEditVideo, handleSaveVideoEdits (usePostsMedia)
  // - handleUploadImage, handleGenerateImage (usePostsMedia)
  // - showPublishOptions, publishPost, handlePublishConfirm, handleMultiPlatformPublish, handleRepublishLinkedIn, handleRepublishMultiPlatform, handleReplyToComment, openMultiPlatformModal, openUnifiedRepublishModal (usePostsPublishing)

  // Handler para criar post manualmente (modal de criação)
  const handleCreatePost = async (postData: {
    content: string;
    platforms: string[];
    hashtags?: string;
    call_to_action?: string;
    call_to_action_url?: string;
    imageFile?: File;
    videoFile?: File;
    videoDestination?: string;
    videoCaption?: string;
    videoHashtags?: string;
    generateImage?: boolean;
    imagePrompt?: string;
    imageProvider?: string;
    generateVideo?: boolean;
    videoPrompt?: string;
    videoDuration?: number;
    videoProvider?: string;
    videoRestrictions?: string;
    videoLogoFile?: File | null;
    videoReferenceImages?: File[];
    videoTexts?: VideoTextItem[];
    videoScript?: string;
    enhanceWithAI?: boolean;
    tempPostId?: number;
    generatedImageUrl?: string; // URL da imagem já gerada
    mediaGallery?: { file: File; type: "image" | "video" }[];
  }) => {
    modals.setCreatingPost(true);
    try {
      // Processar hashtags: aceita tanto formato separado por vírgula quanto por espaço
      let hashtagsArray: string[] = [];
      if (postData.hashtags) {
        const hashtagsStr = postData.hashtags.trim();
        if (hashtagsStr.includes(",")) {
          hashtagsArray = hashtagsStr
            .split(",")
            .map((h) => h.trim())
            .filter((h) => h);
        } else {
          hashtagsArray = hashtagsStr
            .split(/\s+/)
            .map((h) => h.trim())
            .filter((h) => h);
        }
      }

      // Processar hashtags do vídeo
      let videoHashtagsArray: string[] = [];
      if (postData.videoHashtags) {
        const videoHashtagsStr = postData.videoHashtags.trim();
        if (videoHashtagsStr.includes(",")) {
          videoHashtagsArray = videoHashtagsStr
            .split(",")
            .map((h) => h.trim())
            .filter((h) => h);
        } else {
          videoHashtagsArray = videoHashtagsStr
            .split(/\s+/)
            .map((h) => h.trim())
            .filter((h) => h);
        }
      }

      const createdPosts: any[] = [];

      // Se já existe um post temporário com imagem gerada, usar ele
      if (postData.tempPostId) {
        // O post temporário já foi criado e a imagem já foi gerada
        // O post já está no banco de dados, apenas precisamos garantir que apareça na lista
        console.log("✅ Usando post temporário ID:", postData.tempPostId);

        const tempPostId = postData.tempPostId;
        createdPosts.push({ id: tempPostId }); // Adicionar à lista de posts criados

        // Se houver múltiplas plataformas, criar posts adicionais para as outras
        // (o post temporário já foi criado para a primeira plataforma)
        if (postData.platforms.length > 1) {
          console.log(
            `📱 Criando ${postData.platforms.length - 1} posts adicionais para outras plataformas`,
          );
          for (const platform of postData.platforms.slice(1)) {
            // Converter hashtags array para string (backend espera string)
            const hashtagsStr =
              hashtagsArray && hashtagsArray.length > 0
                ? hashtagsArray.join(", ")
                : postData.hashtags || undefined;

            const result = await postsService.createPost({
              content: postData.content,
              platform,
              hashtags: hashtagsStr,
              call_to_action: postData.call_to_action,
              call_to_action_url: postData.call_to_action_url,
            });

            // Extrair postId do resultado
            let newPostId: number | null = null;
            if (result?.id) {
              newPostId = result.id;
            } else if (result?.post?.id) {
              newPostId = result.post.id;
            } else if (result?.data?.id) {
              newPostId = result.data.id;
            }

            if (newPostId) {
              console.log(
                `✅ Post adicional criado para ${platform} com ID:`,
                newPostId,
              );
              createdPosts.push({ id: newPostId });

              // Se houver imagem gerada, copiar para os novos posts também
              // (ou gerar novamente se necessário)
              if (
                postData.generateImage &&
                postData.imagePrompt &&
                postData.generatedImageUrl
              ) {
                try {
                  console.log(
                    `🎨 Gerando imagem para post adicional ${newPostId}...`,
                  );
                  await postsService.generateImageAndWait(newPostId, {
                    prompt: postData.imagePrompt,
                    provider: postData.imageProvider || "dalle",
                  });
                  console.log(
                    `✅ Imagem gerada para post adicional ${newPostId}`,
                  );
                } catch (imageError) {
                  console.warn(
                    "⚠️ Erro ao gerar imagem para post adicional:",
                    imageError,
                  );
                  // Não falhar o processo se não conseguir gerar imagem para posts adicionais
                }
              }
            }
          }
        }

        // Recarregar posts para garantir que apareçam na lista
        // Forçar refresh para garantir que o post temporário apareça
        console.log("🔄 Recarregando lista de posts (forçando refresh)...");
        console.log("📋 Post temporário ID que deveria aparecer:", tempPostId);

        await loadPosts(true); // Forçar refresh do cache

        // Verificar se o post aparece na lista após recarregar
        setTimeout(async () => {
          console.log("🔄 Recarregando novamente após delay...");
          await loadPosts(true);

          // Verificar se o post está na lista
          const pendingResult = await postsService.getPendingPosts(true);
          const allPendingPosts = pendingResult.posts || [];
          const postFound = allPendingPosts.find(
            (p: any) => p.id === tempPostId,
          );
          if (postFound) {
            console.log(
              "✅ Post encontrado na lista após recarregar:",
              postFound,
            );
          } else {
            console.warn(
              "⚠️ Post NÃO encontrado na lista após recarregar. IDs na lista:",
              allPendingPosts.map((p: any) => p.id),
            );
          }
        }, 1000);

        filters.setActiveFilter("pending");
        setSearchParams({ filter: "pending" });
        pagination.setCurrentPage(1);

        const postsCount = createdPosts.length;
        console.log(`✅ ${postsCount} post(s) processado(s) com sucesso`);
        showToastSuccess(
          postsCount > 1
            ? `${postsCount} ${t("posts criados com sucesso! Eles aparecerão na lista de pendentes.")}`
            : t(
                "Post criado com sucesso! Ele aparecerá na lista de pendentes.",
              ),
        );
        return;
      }

      // Criar um post para cada plataforma selecionada
      for (const platform of postData.platforms) {
        // Converter hashtags array para string (backend espera string)
        const hashtagsStr =
          hashtagsArray && hashtagsArray.length > 0
            ? hashtagsArray.join(", ")
            : postData.hashtags || undefined;

        const result = await postsService.createPost({
          content: postData.content,
          platform,
          hashtags: hashtagsStr,
          call_to_action: postData.call_to_action,
          call_to_action_url: postData.call_to_action_url,
        });

        // Extrair o post e ID de forma robusta (o backend pode retornar o post direto ou aninhado)
        const createdPost =
          result?.post || result?.data || (result?.id ? result : null);

        if (createdPost && createdPost.id) {
          createdPosts.push(createdPost);
          const postId = createdPost.id;

          // Upload de imagem legada (campo único)
          if (postData.imageFile) {
            await postsService.uploadImage(postId, postData.imageFile);
          }

          // Upload de vídeo legado (campo único)
          if (postData.videoFile) {
            await postsService.uploadVideo(
              postId,
              postData.videoFile,
              postData.videoDestination || "instagram_feed",
              postData.videoCaption,
              videoHashtagsArray,
            );
          }

          // Upload de Galeria (Múltiplas Imagens/Vídeos)
          if (postData.mediaGallery && postData.mediaGallery.length > 0) {
            console.log(
              `📸 Fazendo upload de ${postData.mediaGallery.length} itens da galeria para o post ${postId}...`,
            );
            for (const item of postData.mediaGallery) {
              if (item.file) {
                try {
                  if (item.type === "image") {
                    await postsService.uploadImage(postId, item.file);
                  } else if (item.type === "video") {
                    await postsService.uploadVideo(
                      postId,
                      item.file,
                      postData.videoDestination || "instagram_feed",
                    );
                  }
                } catch (uploadErr) {
                  console.error(
                    "❌ Erro ao fazer upload de item da galeria:",
                    uploadErr,
                  );
                  // Continuar com os próximos itens mesmo se um falhar
                }
              }
            }
          }

          // Gerar imagem com IA
          if (postData.generateImage && postData.imagePrompt) {
            try {
              const generateResult = await postsService.generateImageAndWait(
                postId,
                {
                  prompt: postData.imagePrompt,
                  provider: postData.imageProvider || "dalle",
                },
              );

              if (!generateResult?.success) {
                const errorMsg =
                  generateResult?.error ||
                  generateResult?.message ||
                  t("Erro desconhecido ao gerar imagem");
                console.error("❌ Erro ao gerar imagem:", errorMsg);
                throw new Error(`${t("Erro ao gerar imagem")}: ${errorMsg}`, {
                  cause: "image-generation",
                });
              }

              console.log("✅ Imagem gerada com sucesso para post", postId);
            } catch (imageError: any) {
              console.error("❌ Erro ao gerar imagem:", imageError);
              // Preservar mensagem de erro original do backend
              const errorMessage =
                imageError?.message ||
                imageError?.error ||
                imageError?.errorData?.error ||
                t("Erro ao gerar imagem com IA");
              // Se a mensagem já contém detalhes (veio do throw acima), não adicionar prefixo redundante
              const finalMessage =
                imageError?.cause === "image-generation"
                  ? errorMessage
                  : `${t("Falha na geração de imagem")}: ${errorMessage}`;
              throw new Error(finalMessage);
            }
          }

          // O bloco de upload de vídeo redundante foi removido.
          // A função postsService.uploadVideo (chamada no bloco legacy) já lida com o envio corretamente.

          // Gerar vídeo com IA
          if (postData.generateVideo) {
            try {
              const body = buildVideoGenerationRequest({
                videoProvider: postData.videoProvider || "veo",
                videoPrompt: postData.videoPrompt,
                videoDuration: postData.videoDuration || 8,
                videoRestrictions: postData.videoRestrictions,
                logoFile: postData.videoLogoFile,
                referenceImages: postData.videoReferenceImages,
                videoTexts: postData.videoTexts,
                videoScript: postData.videoScript,
              });

              const generateResult = await postsService.generateVideoAndWait(
                postId,
                body,
              );

              if (!generateResult?.success) {
                const errorMsg =
                  generateResult?.error ||
                  generateResult?.message ||
                  t("Erro desconhecido ao gerar vídeo");
                console.error("❌ Erro ao gerar vídeo:", errorMsg);
                throw new Error(`${t("Erro ao gerar vídeo")}: ${errorMsg}`, {
                  cause: "video-generation",
                });
              }

              console.log("✅ Vídeo gerado com sucesso para post", postId);
            } catch (videoError: any) {
              console.error("❌ Erro ao gerar vídeo:", videoError);
              const errorMessage =
                videoError?.message ||
                videoError?.error ||
                videoError?.errorData?.error ||
                t("Erro ao gerar vídeo com IA");
              const finalMessage =
                videoError?.cause === "video-generation"
                  ? errorMessage
                  : `${t("Falha na geração de vídeo")}: ${errorMessage}`;
              throw new Error(finalMessage);
            }
          }

          // Melhorar conteúdo com IA (se solicitado)
          if (postData.enhanceWithAI) {
            await postsService.enhancePost(postId);
          }
        }
      }

      await loadPosts();

      // Mudar para a aba de pendentes para ver os novos posts
      filters.setActiveFilter("pending");
      setSearchParams({ filter: "pending" });
      pagination.setCurrentPage(1);

      const postsCount = createdPosts.length;
      showToastSuccess(
        postsCount > 1
          ? `${postsCount} ${t("posts criados com sucesso! Eles aparecerão na lista de pendentes.")}`
          : t("Post criado com sucesso! Ele aparecerá na lista de pendentes."),
      );
    } catch (error: any) {
      showToastError(error.message || t("Erro ao criar post"));
      throw error; // Re-throw para o modal tratar
    } finally {
      modals.setCreatingPost(false);
    }
  };

  // Handler para turbinar post com Meta Ads (Facebook/Instagram) reutilizando endpoint de campanhas pagas
  // Handler para abrir o modal de turbinar post com Meta Ads
  const handleBoostMetaAds = (post: any) => {
    modals.setSelectedPostForBoost(post);
    modals.setShowBoostMetaModal(post.id);
  };

  // Handler para patrocinar post com LinkedIn Ads
  const handleBoostLinkedInAds = (post: any) => {
    modals.setSelectedPostForBoost(post);
    modals.setShowBoostLinkedInModal(post.id);
  };

  // Handler para sincronizar métricas de um post publicado com a plataforma
  const handleSyncMetrics = async (postId: number) => {
    try {
      await apiService.post(`/api/posts/${postId}/sync-metrics`, {});
      showToastSuccess(t("Métricas sincronizadas com sucesso!"));
      await loadPosts(true);
    } catch (error: any) {
      showToastError(error.message || t("Erro ao sincronizar métricas"));
    }
  };

  return (
    <PageLayout
      title={t("Posts")}
      subtitle={t(
        "Crie, edite, aprove e publique suas campanhas em múltiplas plataformas.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-2 py-4"
      loading={loading}
      skeleton={<PostsSkeleton viewMode={viewMode} />}
      actions={
        <div className="flex items-center gap-3">
          <Button
            onClick={() => setShowViralResearchModal(true)}
            variant="outline"
            size="sm"
            className={HEADER_SECONDARY_BUTTON}
            icon={<Sparkles className="h-3.5 w-3.5 text-indigo-600" />}
          >
            <span className="hidden sm:inline text-xs">
              {t("Deep Research")}
            </span>
            <span className="sm:hidden text-xs">{t("Viral")}</span>
          </Button>
          <Button
            onClick={() => modals.setShowCreatePostModal(true)}
            variant="primary"
            size="sm"
            className={HEADER_PRIMARY_BUTTON}
            icon={<Plus className="h-3.5 w-3.5" />}
          >
            <span className="hidden sm:inline text-xs">{t("Novo Post")}</span>
            <span className="sm:hidden text-xs">{t("Novo")}</span>
          </Button>
        </div>
      }
    >
      {/* Action Bar Modernizada - Filter Bar Full-Width */}
      <div className="sticky top-0 z-30 -mt-4 -mx-2 pl-4 pr-2 xl:pl-5 xl:pr-3 py-1.5 bg-white/95 backdrop-blur-md border-b border-purple-200 flex items-center justify-between">
        <div className="flex flex-row flex-wrap gap-3 items-center w-full">
          <div className="flex-1 min-w-[200px]">
            <Input
              type="text"
              placeholder={t("Buscar nos posts...")}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                pagination.setCurrentPage(1);
              }}
              inputSize="sm"
              icon={<Search className="h-4 w-4 text-gray-400" />}
              className="w-full h-8! sm:h-9!"
            />
          </div>

          <div className="w-[215px] shrink-0">
            <Select
              value={filters.activeFilter}
              showSelectedCount
              onChange={(v) => {
                filters.setActiveFilter(v as any);
                pagination.setCurrentPage(1);
              }}
              options={[
                {
                  value: "all",
                  label: t("Todos status"),
                  icon: <ListFilter />,
                  count: statusCounts.all,
                },
                {
                  value: "pending",
                  label: t("Pendentes"),
                  icon: <Clock />,
                  count: statusCounts.pending,
                },
                {
                  value: "approved",
                  label: t("Aprovados"),
                  icon: <CheckCircle />,
                  count: statusCounts.approved,
                },
                {
                  value: "scheduled",
                  label: t("Agendados"),
                  icon: <CalendarClock />,
                  count: statusCounts.scheduled,
                },
                {
                  value: "published",
                  label: t("Publicados"),
                  icon: <Send />,
                  count: statusCounts.published,
                },
                {
                  value: "rejected",
                  label: t("Rejeitados"),
                  icon: <XCircle />,
                  count: statusCounts.rejected,
                },
              ]}
              size="sm"
            />
          </div>

          <div className="w-[245px] shrink-0">
            <Select
              value={filters.platformFilter}
              showSelectedCount
              onChange={(v) => {
                filters.setPlatformFilter(v as any);
                pagination.setCurrentPage(1);
              }}
              options={[
                {
                  value: "all",
                  label: t("Todas plataformas"),
                  icon: <Globe />,
                  count: platformCounts.all,
                },
                {
                  value: "linkedin",
                  label: t("LinkedIn"),
                  icon: <PlatformIcon platform="linkedin" />,
                  count: platformCounts.linkedin,
                },
                {
                  value: "facebook",
                  label: t("Facebook"),
                  icon: <PlatformIcon platform="facebook" />,
                  count: platformCounts.facebook,
                },
                {
                  value: "instagram",
                  label: t("Instagram"),
                  icon: <PlatformIcon platform="instagram" />,
                  count: platformCounts.instagram,
                },
              ]}
              size="sm"
            />
          </div>

          <div className="w-[210px] shrink-0">
            <Select
              value={filters.aiGeneratedFilter}
              showSelectedCount
              onChange={(v) => {
                filters.setAiGeneratedFilter(v as any);
                pagination.setCurrentPage(1);
              }}
              options={[
                {
                  value: "all",
                  label: t("Toda autoria"),
                  icon: <ListFilter />,
                  count: aiGeneratedCounts.all,
                },
                {
                  value: "yes",
                  label: t("Gerados por IA"),
                  icon: <Bot />,
                  count: aiGeneratedCounts.yes,
                },
                {
                  value: "no",
                  label: t("Humanos"),
                  icon: <User />,
                  count: aiGeneratedCounts.no,
                },
              ]}
              size="sm"
            />
          </div>

          {filters.hasActiveFilters && (
            <div className="shrink-0">
              <Button
                onClick={() => {
                  filters.clearAllFilters();
                  setSearchTerm("");
                  pagination.setCurrentPage(1);
                }}
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-red-600 hover:bg-red-50 px-2"
                title={t("Limpar filtros")}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          <div className="flex items-center bg-gray-100/80 p-1 rounded-xl shadow-inner border border-gray-200/50 shrink-0 ml-auto">
            <button
              onClick={() => setViewMode("cards")}
              className={cn(
                "p-1.5 rounded-lg transition-all duration-200 flex items-center justify-center",
                viewMode === "cards"
                  ? "bg-white text-purple-600 shadow-xs"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-200/50",
              )}
              title={t("Visão em Cards")}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={cn(
                "p-1.5 rounded-lg transition-all duration-200 flex items-center justify-center",
                viewMode === "table"
                  ? "bg-white text-purple-600 shadow-xs"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-200/50",
              )}
              title={t("Visão em Lista")}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Posts Content */}
      {currentPosts.length === 0 ? (
        <EmptyState
          icon={utils.getEmptyStateIcon(filters.activeFilter)}
          title={`${t("Nenhum post")} ${utils.getFilterLabel(filters.activeFilter).toLowerCase()}`}
          description={`${t("Não há posts")} ${utils.getFilterLabel(filters.activeFilter).toLowerCase()} ${t("no momento.")}`}
          action={
            <Button
              onClick={() => modals.setShowCreatePostModal(true)}
              variant="primary"
              size="sm"
              icon={<Send className="h-4 w-4" />}
            >
              {t("Criar Post")}
            </Button>
          }
        />
      ) : (
        (() => {
          const renderedPosts = paginatedPosts.map((post) => {
            if (!post) return null;
            // Determinar plataforma e ID para posts publicados (comentários)
            let platform: "instagram" | "facebook" | "linkedin" | null = null;
            let postPlatformId: string | null = null;
            let postUrn: string | undefined = undefined;

            if (filters.activeFilter === "published") {
              const publishedPost = post as any;
              if (publishedPost.instagram_post_id) {
                platform = "instagram";
                postPlatformId = publishedPost.instagram_post_id;
              } else if (publishedPost.facebook_post_id) {
                platform = "facebook";
                postPlatformId = publishedPost.facebook_post_id;
              } else if (publishedPost.linkedin_post_id) {
                platform = "linkedin";
                postPlatformId = publishedPost.linkedin_post_id;
                postUrn = publishedPost.linkedin_post_id.startsWith("urn:")
                  ? publishedPost.linkedin_post_id
                  : undefined;
              } else if (publishedPost.platform) {
                const platformLower = String(
                  publishedPost.platform,
                ).toLowerCase();
                if (platformLower.includes("instagram")) {
                  platform = "instagram";
                } else if (platformLower.includes("facebook")) {
                  platform = "facebook";
                } else if (platformLower.includes("linkedin")) {
                  platform = "linkedin";
                }
              }
            }

            const PostComponent: any =
              viewMode === "cards" ? PostCard : PostTableRow;

            return (
              <PostComponent
                key={post.id}
                post={post as any}
                onPreview={() => modals.setShowPreviewModal(post.id)}
                onApprove={
                  filters.activeFilter === "pending"
                    ? () => actions.handleApprove(post.id)
                    : undefined
                }
                onReject={
                  filters.activeFilter === "pending"
                    ? () => {
                        modals.setRejectPostId(post.id);
                        modals.setRejectModalOpen(true);
                      }
                    : undefined
                }
                onRevertToPending={
                  filters.activeFilter === "approved"
                    ? () => actions.handleRevertToPending(post.id)
                    : undefined
                }
                onEdit={() => {
                  modals.setEditingPost(post.id);
                  modals.setEditContent(post.content);
                  // Converter hashtags para string (pode vir como array ou string)
                  // Adicionar "#" na exibição para melhor UX
                  let hashtagsStr = "";
                  if (
                    Array.isArray(post.hashtags) &&
                    post.hashtags.length > 0
                  ) {
                    // Se for array, adicionar # e juntar com espaços
                    hashtagsStr = post.hashtags
                      .map((tag: any) => {
                        const cleanTag = String(tag)
                          .trim()
                          .replace(/^[{#]+/, "")
                          .replace(/}+$/, "");
                        return cleanTag ? `#${cleanTag}` : "";
                      })
                      .filter((tag: string) => tag.length > 0)
                      .join(" ");
                  } else if (
                    typeof post.hashtags === "string" &&
                    post.hashtags.trim()
                  ) {
                    // Se for string, pode estar separada por vírgulas (formato do banco: "tag1,tag2,tag3")
                    // Normalizar: remover # existente, separar por vírgulas, limpar, adicionar # e juntar com espaços
                    const hashtagsString = post.hashtags.trim();
                    const tags = hashtagsString
                      .split(",")
                      .map((tag: string) => {
                        const cleanTag = tag
                          .trim()
                          .replace(/^[{#]+/, "")
                          .replace(/}+$/, "")
                          .replace(/['"]/g, "");
                        return cleanTag;
                      })
                      .filter((tag: string) => tag.length > 0)
                      .map((tag: string) => `#${tag}`);
                    hashtagsStr = tags.join(" ");
                  } else if (post.hashtags) {
                    // Se for outro tipo, tentar converter e adicionar #
                    const cleanTag = String(post.hashtags)
                      .trim()
                      .replace(/^[{#]+/, "")
                      .replace(/}+$/, "");
                    hashtagsStr = cleanTag ? `#${cleanTag}` : "";
                  }
                  modals.setEditHashtags(hashtagsStr);
                  modals.setEditCallToAction(post.call_to_action || "");
                  modals.setEditCallToActionUrl(post.call_to_action_url || "");

                  // Carregar galeria para edição
                  const currentGallery: Array<{
                    file: File | null;
                    preview: string;
                    type: "image" | "video";
                    id?: string;
                  }> = [];
                  if (post.media_gallery && Array.isArray(post.media_gallery)) {
                    post.media_gallery.forEach((item: any) => {
                      currentGallery.push({
                        file: null,
                        preview: item.public_url || item.url || "",
                        type: item.type || "image",
                        id: item.id,
                      });
                    });
                  } else {
                    // Fallback para campos individuais
                    if (
                      post.image_info &&
                      (post.image_info.public_url || post.image_info.filename)
                    ) {
                      currentGallery.push({
                        file: null,
                        preview: getPostImageUrl(post.image_info),
                        type: "image",
                      });
                    }
                    if (
                      post.video_info &&
                      (post.video_info.public_url || post.video_info.filename)
                    ) {
                      currentGallery.push({
                        file: null,
                        preview: "",
                        type: "video",
                      });
                    }
                  }
                  modals.setEditGalleryItems(currentGallery);
                }}
                onSchedule={
                  filters.activeFilter === "approved" ||
                  filters.activeFilter === "pending"
                    ? () => {
                        modals.setShowScheduleModal(post.id);
                        const tomorrow = new Date();
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        modals.setScheduleDate(
                          tomorrow.toISOString().split("T")[0],
                        );
                      }
                    : undefined
                }
                onUnschedule={() => actions.handleUnschedule(post.id)}
                onDelete={() => modals.setShowDeleteModal(post.id)}
                onPublish={
                  filters.activeFilter === "approved" &&
                  (post.status === "approved" || post.status === "scheduled") &&
                  !(post as any).published_at
                    ? () => publishing.showPublishOptions(post.id)
                    : undefined
                }
                onPublishMulti={
                  filters.activeFilter === "approved"
                    ? () => publishing.openMultiPlatformModal(post)
                    : undefined
                }
                onRepublish={
                  filters.activeFilter === "published"
                    ? () => publishing.openUnifiedRepublishModal(post)
                    : undefined
                }
                onReschedule={
                  filters.activeFilter === "published"
                    ? () => {
                        modals.setShowScheduleModal(post.id);
                        const tomorrow = new Date();
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        modals.setScheduleDate(
                          tomorrow.toISOString().split("T")[0],
                        );
                      }
                    : undefined
                }
                onUploadImage={
                  post.status === "pending" || post.status === "approved"
                    ? () => {
                        modals.setImageModalMode("upload");
                        modals.setShowImageModal(post.id);
                      }
                    : undefined
                }
                onUploadVideo={
                  post.status === "pending" || post.status === "approved"
                    ? () => modals.setShowVideoModal(post.id)
                    : undefined
                }
                onEditVideo={
                  post.video_info &&
                  (post.video_info.filename || post.video_info.public_url)
                    ? () => media.handleEditVideo(post.id)
                    : undefined
                }
                onGenerateVideo={
                  post.status === "pending" || post.status === "approved"
                    ? () => {
                        media.setVideoPrompt("");
                        media.setVideoDuration(8);
                        modals.setShowVideoGenerateModal(post.id);
                      }
                    : undefined
                }
                onClearVideo={() => media.handleClearVideo(post.id)}
                onClearImage={() => media.handleClearImage(post.id)}
                onGenerateCreative={() => {
                  modals.setCreativeEditorPost(post);
                  modals.setShowCreativeEditorModal(post.id);
                }}
                onBoostMetaAds={() => handleBoostMetaAds(post)}
                onBoostLinkedInAds={() => handleBoostLinkedInAds(post)}
                postMetrics={(post as any).metrics}
                onSyncMetrics={
                  filters.activeFilter === "published" &&
                  post.status === "published"
                    ? () => handleSyncMetrics(post.id)
                    : undefined
                }
                onViewVariations={(variations: any, originalUrl: string) => {
                  modals.setSelectedVariations(variations);
                  modals.setSelectedOriginalUrl(originalUrl);
                  modals.setSelectedPostForVariations(post);
                  modals.setShowSocialVariationsModal(true);
                }}
                loadingStates={{
                  approving: loading,
                  rejecting: modals.rejectPostId === post.id,
                  editing: modals.editingPost === post.id,
                  deleting: modals.showDeleteModal === post.id,
                  scheduling: modals.schedulingPost === post.id,
                  publishing: modals.publishingPost === post.id,
                  uploadingImage: modals.uploadingImage === post.id,
                  generatingVideo: modals.generatingVideo === post.id,
                  enhancing: modals.enhancingPost === post.id,
                  republishing: modals.republishingPost === post.id,
                }}
                highlighted={highlightedPost === post.id}
                innerRef={(el: HTMLDivElement | null) => {
                  postRefs.current[post.id] = el;
                  return undefined;
                }}
                showEngagementColumn={filters.activeFilter === "published"}
              >
                {/* Otimizar com IA - Menu unificado */}
                {filters.activeFilter === "pending" && (
                  <AIOptimizerMenu
                    onEnhanceText={() => ai.enhancePost(post.id)}
                    onGenerateImage={() =>
                      ai.generateImage(post.id, !!post.content?.trim())
                    }
                    onGenerateVideo={() => {
                      media.setVideoPrompt("");
                      media.setVideoDuration(8);
                      modals.setShowVideoGenerateModal(post.id);
                    }}
                    onEnhanceImage={
                      post.image_info &&
                      (post.image_info.filename || post.image_info.public_url)
                        ? () =>
                            ai.openEnhanceImageModal(
                              post.id,
                              getPostImageUrl(post.image_info),
                            )
                        : undefined
                    }
                    onGenerateCreative={() => {
                      modals.setCreativeEditorPost(post);
                      modals.setShowCreativeEditorModal(post.id);
                    }}
                    onVisualSuggestions={() =>
                      ai.openVisualSuggestionsModal(post.id)
                    }
                    onCheckGrammar={() => ai.checkGrammar(post.id)}
                    hasVisualSuggestions={!!showVisualSuggestions[post.id]}
                    loadingEnhance={modals.enhancingPost === post.id}
                    loadingImage={modals.generatingImage === post.id}
                    loadingVideo={modals.generatingVideo === post.id}
                    loadingEnhanceImage={ai.enhancingImage === post.id}
                    loadingGrammar={ai.checkingGrammar === post.id}
                  />
                )}

                {/* CommentsList para Posts Publicados */}
                {filters.activeFilter === "published" &&
                  (post.status === "published" || !!post.published_at) &&
                  platform && (
                    <CommentsList
                      platform={platform}
                      postId={postPlatformId || ""}
                      postUrn={postUrn}
                      onReply={(commentId: string, commentText: string) => {
                        if (platform) {
                          publishing.handleReplyToComment(
                            platform,
                            commentId,
                            commentText,
                            postUrn,
                          );
                        }
                      }}
                    />
                  )}
              </PostComponent>
            );
          });

          return viewMode === "cards" ? (
            <ResponsiveGrid
              cols={{ default: 1, md: 2, lg: 3, xl: 4 }}
              className="gap-2! mt-2!"
            >
              {renderedPosts}
            </ResponsiveGrid>
          ) : (
            <div className="bg-white rounded-2xl shadow-xs border border-gray-200 mt-2!">
              <table className="w-full text-left border-collapse table-fixed min-w-[800px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-xs font-bold text-gray-600 uppercase tracking-wider">
                    <th className="px-4 py-3">{t("Post")}</th>
                    <th className="px-4 py-3 w-[80px]">{t("Rede")}</th>
                    <th className="px-4 py-3 w-[210px] whitespace-nowrap">
                      {t("Status")}
                    </th>
                    {filters.activeFilter === "published" && (
                      <th className="px-4 py-3 w-[150px]">
                        {t("Engajamento")}
                      </th>
                    )}
                    {filters.activeFilter === "pending" && (
                      <th className="px-4 py-3 w-[230px] whitespace-nowrap">
                        {t("Otimizar com IA")}
                      </th>
                    )}
                    {filters.activeFilter === "published" && (
                      <th className="px-4 py-3 w-[200px] whitespace-nowrap">
                        {t("Comentários")}
                      </th>
                    )}
                    <th className="px-4 py-3 text-right w-[80px]">
                      {t("Ações")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {renderedPosts}
                </tbody>
              </table>
            </div>
          );
        })()
      )}

      {/* Paginação - sempre visível */}
      <Pagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={pagination.handlePageChange}
        showInfo={true}
        maxVisiblePages={5}
      />

      {/* Todos os modais estão no componente PostsModals */}
      <PostsModals
        modals={modals}
        actions={actions}
        ai={ai}
        media={media}
        publishing={publishing}
        loading={loading}
        currentPosts={currentPosts}
        pendingPosts={pendingPosts}
        approvedPosts={approvedPosts}
        publishedPosts={publishedPosts}
        loadPosts={loadPosts}
        handleReject={handleReject}
        handleSchedule={handleSchedule}
        handleEnhancePost={handleEnhancePost}
        openVisualSuggestionsModal={ai.openVisualSuggestionsModal}
        hasVisualSuggestionsForPostId={(id) => !!showVisualSuggestions[id]}
        handleCreatePost={handleCreatePost}
      />

      {/* Sidebar de Filtros */}
      <VisualSuggestionsModal
        open={visualSuggestionsModalPostId !== null}
        onClose={() => setVisualSuggestionsModalPostId(null)}
        postId={visualSuggestionsModalPostId}
        data={
          visualSuggestionsModalPostId != null
            ? showVisualSuggestions[visualSuggestionsModalPostId]
            : null
        }
        onGenerate={(postId, model) => ai.getVisualSuggestions(postId, model)}
        loading={ai.generatingVisualSuggestions}
      />

      <ViralResearchModal
        open={showViralResearchModal}
        onClose={() => setShowViralResearchModal(false)}
        onUseContent={(content) => {
          modals.setShowCreatePostModal(true);
          // O CreatePostModal precisa receber o texto.
          // Se o hook/modal de criação não tiver um state exposto, uma solução rápida
          // é passar via localStorage temporário ou prop.
          // Aqui no Posts.tsx, infelizmente o estado do CreatePostModal é interno a ele,
          // mas vamos colocar no event_bus ou simular um input.
          // Uma forma melhor: Setar no localstorage para que o modal ao montar pegue.
          localStorage.setItem("viral_research_content", content);

          // E disparar evento customizado
          window.dispatchEvent(
            new CustomEvent("viral_research_content_ready", {
              detail: content,
            }),
          );
        }}
      />
    </PageLayout>
  );
};

export default Posts;
